import {
  Entity,
  FieldType,
  FieldValue,
  OptionsetValue,
  ViewColumnBehaviour,
  ViewColumnDefinition,
} from '@msdk/definitions'
import React, { ReactNode } from 'react'

import { CustomCellRenderer } from './CustomCellRenderer'
import { DateTimeCellRenderer } from './DateTimeCellRenderer'
import { NumericCellRenderer } from './NumericCellRenderer'
import { OptionsetCellRenderer } from './OptionsetCellRenderer'
import { StringCellRenderer } from './StringCellRenderer'

interface Props {
  field: FieldType
  value: FieldValue<any> | null
  col: ViewColumnDefinition
  row?: Entity
  onItemClick?: () => void
}

export const ConditionalCellRenderer = ({ field, value, col, row, onItemClick }: Props) => {
  const handleItemClick = () => {
    onItemClick && onItemClick()
  }

  const isClickable = col.behaviour === ViewColumnBehaviour.Clickable

  const renderedComponent = (
    <StringCellRenderer value={value} onItemClick={handleItemClick} clickable={isClickable} ellipsis={col.ellipsis} />
  )

  const renderer: Record<FieldType, ReactNode> = {
    Attachment: renderedComponent,
    Bag: null,
    Custom: <CustomCellRenderer value={value} col={col} row={row} onItemClick={onItemClick} />,
    DateTime: (
      <DateTimeCellRenderer
        value={value}
        onItemClick={handleItemClick}
        clickable={isClickable}
        ellipsis={col.ellipsis}
      />
    ),
    DateTimeUnknown: (
      <DateTimeCellRenderer
        value={value}
        onItemClick={handleItemClick}
        clickable={isClickable}
        ellipsis={col.ellipsis}
        allowUnknown
      />
    ),
    Duration: renderedComponent,
    Image: renderedComponent,
    List: renderedComponent,
    Lookup: renderedComponent,
    MetadataSelector: renderedComponent,
    MultiSelector: renderedComponent,
    Numeric: (
      <NumericCellRenderer
        value={value}
        colDef={col}
        clickable={isClickable}
        ellipsis={col.ellipsis}
        onItemClick={handleItemClick}
      />
    ),
    OptionSet: <OptionsetCellRenderer value={value as OptionsetValue} colDef={col} onItemClick={onItemClick} />,
    String: renderedComponent,
    Time: (
      <DateTimeCellRenderer
        value={value}
        onItemClick={handleItemClick}
        clickable={isClickable}
        ellipsis={col.ellipsis}
      />
    ),
    AnyOf: renderedComponent,
    CodeableConcept: renderedComponent,
  }

  return <>{renderer[field]}</>
}
